import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Divider, IconButton, Paper, TextField, Typography } from '@mui/material';
import { Chat, CheckCircle, Close, GraphicEq, Logout, People, Send, Share, VideoCall, Videocam, VolumeOff } from '@mui/icons-material';
import { addDoc, collection, deleteDoc, doc, getDoc, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { auth, db } from '../../config/firebase';
import employeeMeetingService from '../../services/api/employeeMeetingService';
import { friendlyError } from '../../utils/userMessages';

const C = { bg: '#0E1724', panel: '#172333', panel2: '#1D2C3F', text: '#F5F8FA', soft: '#AAB7C5', line: 'rgba(255,255,255,.09)', blue: '#4BA3D3', green: '#42B89B', red: '#E87461' };
const rtcConfig = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }] };
const normalizeRole = (role) => String(role || '').toLowerCase().replace(/[\s-]+/g, '_');

function VideoTile({ stream, label, muted = false }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && stream) ref.current.srcObject = stream; }, [stream]);
  return (
    <Paper elevation={0} sx={{ position: 'relative', overflow: 'hidden', borderRadius: 3, bgcolor: '#050A10', minHeight: 220, border: `1px solid ${C.line}` }}>
      <video ref={ref} autoPlay playsInline muted={muted} aria-label={`${label} live video`} style={{ width: '100%', height: '100%', minHeight: 220, objectFit: 'cover', display: 'block' }}>
        <track kind="captions" src="data:text/vtt,WEBVTT%0A%0A" srcLang="en" label="Live meeting captions" default />
      </video>
      <Chip size="small" label={label} sx={{ position: 'absolute', left: 10, bottom: 10, bgcolor: 'rgba(0,0,0,.58)', color: '#fff', fontWeight: 800 }} />
    </Paper>
  );
}

export default function EmployeeMeetingRoom() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [login, setLogin] = useState({ email: '', password: '' });
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [participants, setParticipants] = useState({});
  const [chat, setChat] = useState([]);
  const [message, setMessage] = useState('');
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [sharing, setSharing] = useState(false);
  const peers = useRef(new Map());
  const pendingCandidates = useRef(new Map());
  const started = useRef(false);
  const leaving = useRef(false);
  const cameraTrackRef = useRef(null);
  const screenTrackRef = useRef(null);

  const currentUid = auth.currentUser?.uid;
  const normalizedUserRole = normalizeRole(userData?.role || userData?.userRole);
  const isSuperAdmin = ['super_admin', 'superadmin'].includes(normalizedUserRole);
  const isHost = Boolean(currentUid && (currentUid === meeting?.hostId || isSuperAdmin));

  const ensurePeer = useCallback((otherUid) => {
    if (peers.current.has(otherUid)) return peers.current.get(otherUid);
    const pc = new RTCPeerConnection(rtcConfig);
    if (localStream) localStream.getTracks().forEach((track) => pc.addTrack(track, localStream));
    pc.onicecandidate = async (event) => {
      if (!event.candidate || !auth.currentUser?.uid) return;
      try {
        await addDoc(collection(db, 'employeeMeetings', meetingId, 'signals'), { from: auth.currentUser.uid, to: otherUid, type: 'candidate', candidate: event.candidate.toJSON(), createdAt: serverTimestamp() });
      } catch (error) {
        console.warn('[employee meeting candidate write]', error);
      }
    };
    pc.ontrack = (event) => {
      const stream = event.streams?.[0] || new MediaStream([event.track]);
      setRemoteStreams((previous) => ({ ...previous, [otherUid]: stream }));
    };
    pc.onconnectionstatechange = () => {
      if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
        setRemoteStreams((previous) => { const next = { ...previous }; delete next[otherUid]; return next; });
      }
    };
    peers.current.set(otherUid, pc);
    return pc;
  }, [localStream, meetingId]);

  const flushCandidates = async (otherUid, pc) => {
    const rows = pendingCandidates.current.get(otherUid) || [];
    for (const candidate of rows) {
      try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); }
      catch (error) { console.warn('[employee meeting candidate]', error); }
    }
    pendingCandidates.current.delete(otherUid);
  };

  const makeOffer = useCallback(async (otherUid) => {
    const pc = ensurePeer(otherUid);
    if (pc.signalingState !== 'stable') return;
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await addDoc(collection(db, 'employeeMeetings', meetingId, 'signals'), { from: auth.currentUser.uid, to: otherUid, type: 'offer', sdp: { type: offer.type, sdp: offer.sdp }, createdAt: serverTimestamp() });
  }, [ensurePeer, meetingId]);

  const stopMedia = useCallback(() => {
    screenTrackRef.current?.stop?.();
    screenTrackRef.current = null;
    cameraTrackRef.current?.stop?.();
    cameraTrackRef.current = null;
    localStream?.getTracks().forEach((track) => track.stop());
    peers.current.forEach((pc) => pc.close());
    peers.current.clear();
  }, [localStream]);

  const leave = useCallback(async (endMeeting = false, remoteEnded = false) => {
    if (leaving.current) return;
    leaving.current = true;
    const uid = auth.currentUser?.uid;
    try {
      if (uid) await deleteDoc(doc(db, 'employeeMeetings', meetingId, 'participants', uid)).catch((error) => console.warn('[employee meeting participant cleanup]', error));
      if (endMeeting && !remoteEnded) await employeeMeetingService.completeEmployeeMeeting(meetingId);
    } catch (error) {
      if (!remoteEnded) toast.error(friendlyError(error, 'Unable to end the meeting cleanly.'));
    } finally {
      stopMedia();
      if (isSuperAdmin) navigate('/employee-meetings', { replace: true });
      else {
        await signOut(auth).catch((error) => console.warn('[employee meeting sign out]', error));
        navigate(`/employee-room/${meetingId}`, { replace: true });
      }
    }
  }, [isSuperAdmin, meetingId, navigate, stopMedia]);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    started.current = false;
    leaving.current = false;
    try {
      const user = auth.currentUser;
      if (!user) { setMeeting(null); setUserData(null); setLoading(false); return; }
      const [userSnap, room] = await Promise.all([getDoc(doc(db, 'users', user.uid)), employeeMeetingService.getEmployeeMeeting(meetingId)]);
      if (!userSnap.exists() || !room) throw new Error('Employee or meeting record was not found.');
      const profile = userSnap.data() || {};
      if (profile.isBlocked === true || profile.isActive === false) throw new Error('Your employee access is disabled.');
      setUserData(profile);
      setMeeting(room);
      const media = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      cameraTrackRef.current = media.getVideoTracks()[0] || null;
      setMicOn(media.getAudioTracks().some((track) => track.enabled));
      setCamOn(Boolean(cameraTrackRef.current?.enabled));
      setLocalStream(media);
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to open employee meeting.'));
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  useEffect(() => {
    if (!localStream || !auth.currentUser || started.current) return undefined;
    started.current = true;
    const uid = auth.currentUser.uid;

    setDoc(doc(db, 'employeeMeetings', meetingId, 'participants', uid), { uid, displayName: userData?.displayName || userData?.email || 'Employee', joinedAt: serverTimestamp(), role: userData?.role || 'platform_employee' }, { merge: true }).catch((error) => console.warn('[employee meeting presence]', error));

    const unsubMeeting = onSnapshot(doc(db, 'employeeMeetings', meetingId), (snapshot) => {
      if (!snapshot.exists()) return;
      const room = { id: snapshot.id, ...snapshot.data() };
      setMeeting(room);
      if (room.status === 'completed' && !leaving.current) {
        toast.success('The meeting has ended.');
        leave(false, true);
      }
    }, (error) => console.warn('[employee meeting status listener]', error));

    const unsubParticipants = onSnapshot(collection(db, 'employeeMeetings', meetingId, 'participants'), (snapshot) => {
      const map = {};
      snapshot.docs.forEach((row) => {
        map[row.id] = row.data();
        if (row.id !== uid && !peers.current.has(row.id) && uid > row.id) makeOffer(row.id).catch((error) => console.warn('[employee meeting offer]', error));
      });
      const activePeerIds = new Set(Object.keys(map).filter((id) => id !== uid));
      peers.current.forEach((pc, peerId) => {
        if (!activePeerIds.has(peerId)) {
          pc.close();
          peers.current.delete(peerId);
          pendingCandidates.current.delete(peerId);
          setRemoteStreams((previous) => { const next = { ...previous }; delete next[peerId]; return next; });
        }
      });
      setParticipants(map);
    }, (error) => console.warn('[employee meeting participant listener]', error));

    const signalQuery = query(collection(db, 'employeeMeetings', meetingId, 'signals'), where('to', '==', uid));
    const unsubSignals = onSnapshot(signalQuery, async (snapshot) => {
      for (const change of snapshot.docChanges()) {
        if (change.type !== 'added') continue;
        const signal = change.doc.data() || {};
        const from = signal.from;
        if (!from) { await deleteDoc(change.doc.ref).catch((error) => console.warn('[employee meeting invalid signal cleanup]', error)); continue; }
        try {
          const pc = ensurePeer(from);
          if (signal.type === 'offer') {
            await pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
            await flushCandidates(from, pc);
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            await addDoc(collection(db, 'employeeMeetings', meetingId, 'signals'), { from: uid, to: from, type: 'answer', sdp: { type: answer.type, sdp: answer.sdp }, createdAt: serverTimestamp() });
          } else if (signal.type === 'answer') {
            await pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
            await flushCandidates(from, pc);
          } else if (signal.type === 'candidate' && signal.candidate) {
            if (pc.remoteDescription) await pc.addIceCandidate(new RTCIceCandidate(signal.candidate));
            else pendingCandidates.current.set(from, [...(pendingCandidates.current.get(from) || []), signal.candidate]);
          }
        } catch (error) {
          console.warn('[employee meeting signal]', error);
        } finally {
          await deleteDoc(change.doc.ref).catch((error) => console.warn('[employee meeting signal cleanup]', error));
        }
      }
    }, (error) => console.warn('[employee meeting signal listener]', error));

    const unsubChat = onSnapshot(collection(db, 'employeeMeetings', meetingId, 'chat'), (snapshot) => {
      const rows = snapshot.docs.map((row) => ({ id: row.id, ...row.data() })).sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      setChat(rows);
    }, (error) => console.warn('[employee meeting chat listener]', error));

    return () => {
      unsubMeeting();
      unsubParticipants();
      unsubSignals();
      unsubChat();
      deleteDoc(doc(db, 'employeeMeetings', meetingId, 'participants', uid)).catch((error) => console.warn('[employee meeting presence cleanup]', error));
      peers.current.forEach((pc) => pc.close());
      peers.current.clear();
      localStream.getTracks().forEach((track) => track.stop());
    };
  }, [ensurePeer, leave, localStream, makeOffer, meetingId, userData]);

  const submitLogin = async () => {
    try { await signInWithEmailAndPassword(auth, login.email.trim(), login.password); await bootstrap(); }
    catch (error) { toast.error(friendlyError(error, 'Unable to sign in to the employee meeting.')); }
  };

  const toggleMic = () => {
    localStream?.getAudioTracks().forEach((track) => { track.enabled = !micOn; });
    setMicOn((value) => !value);
  };

  const toggleCam = () => {
    const track = cameraTrackRef.current || localStream?.getVideoTracks()?.[0];
    if (track) track.enabled = !camOn;
    setCamOn((value) => !value);
  };

  const restoreCamera = useCallback(async () => {
    const cameraTrack = cameraTrackRef.current;
    if (!cameraTrack || cameraTrack.readyState === 'ended') {
      try {
        const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
        cameraTrackRef.current = cameraStream.getVideoTracks()[0] || null;
      } catch (error) {
        toast.error(friendlyError(error, 'Unable to restore your camera.'));
        setSharing(false);
        return;
      }
    }
    const track = cameraTrackRef.current;
    if (!track) { setSharing(false); return; }
    track.enabled = camOn;
    await Promise.all([...peers.current.values()].map(async (pc) => {
      const sender = pc.getSenders().find((item) => item.track?.kind === 'video');
      if (sender) await sender.replaceTrack(track);
    }));
    const audioTracks = localStream?.getAudioTracks() || [];
    setLocalStream(new MediaStream([...audioTracks, track]));
    if (screenTrackRef.current && screenTrackRef.current.readyState !== 'ended') screenTrackRef.current.stop();
    screenTrackRef.current = null;
    setSharing(false);
  }, [camOn, localStream]);

  const shareScreen = async () => {
    try {
      if (sharing) { await restoreCamera(); return; }
      const screen = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const track = screen.getVideoTracks()[0];
      if (!track) return;
      screenTrackRef.current = track;
      await Promise.all([...peers.current.values()].map(async (pc) => {
        const sender = pc.getSenders().find((item) => item.track?.kind === 'video');
        if (sender) await sender.replaceTrack(track);
      }));
      const audioTracks = localStream?.getAudioTracks() || [];
      setLocalStream(new MediaStream([...audioTracks, track]));
      setSharing(true);
      track.onended = () => { restoreCamera().catch((error) => console.warn('[employee meeting screen restore]', error)); };
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to share your screen.'));
    }
  };

  const sendChat = async () => {
    if (!message.trim() || !auth.currentUser) return;
    try { await employeeMeetingService.addMeetingChat(meetingId, message); setMessage(''); }
    catch (error) { toast.error(friendlyError(error, 'Unable to send message.')); }
  };

  if (loading) return <Box sx={{ minHeight: '100vh', bgcolor: C.bg, display: 'grid', placeItems: 'center' }}><CircularProgress sx={{ color: '#fff' }} /></Box>;

  if (!auth.currentUser) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, bgcolor: '#F1F6F9', backgroundImage: 'radial-gradient(circle at 12% 12%,rgba(46,111,142,.14),transparent 30%),radial-gradient(circle at 88% 85%,rgba(66,184,155,.13),transparent 30%)' }}>
        <Paper elevation={0} sx={{ width: '100%', maxWidth: 430, p: 3, borderRadius: 5, border: '1px solid rgba(31,62,82,.09)', boxShadow: '0 28px 70px -38px rgba(24,34,53,.32)', position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', right: -65, top: -75, width: 175, height: 175, borderRadius: '50%', border: '28px solid rgba(46,111,142,.055)' } }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}><Box sx={{ width: 55, height: 55, borderRadius: 3.5, display: 'grid', placeItems: 'center', color: '#fff', background: 'linear-gradient(135deg,#2E6F8E,#42B89B)', boxShadow: '0 14px 30px -16px rgba(46,111,142,.9)', mb: 2 }}><VideoCall /></Box><Typography sx={{ color: '#182235', fontSize: '1.48rem', fontWeight: 900, letterSpacing: '-.035em' }}>Employee meeting room</Typography><Typography sx={{ color: '#53677B', fontSize: '.78rem', mt: .55, mb: 2, lineHeight: 1.55 }}>Sign in with your TELCOFAIR platform employee account to join this internal meeting.</Typography><TextField fullWidth label="Employee email" type="email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} sx={{ mb: 1.2 }} /><TextField fullWidth label="Password" type="password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') submitLogin(); }} /><Button fullWidth variant="contained" startIcon={<VideoCall />} onClick={submitLogin} sx={{ mt: 1.6, py: 1.1, borderRadius: 3, fontWeight: 850, textTransform: 'none', background: 'linear-gradient(135deg,#2E6F8E,#42B89B)' }}>Join secure room</Button></Box>
        </Paper>
      </Box>
    );
  }

  if (!meeting || !localStream) {
    return <Box sx={{ minHeight: '100vh', bgcolor: C.bg, color: C.text, display: 'grid', placeItems: 'center', p: 3, textAlign: 'center' }}><Box><Close sx={{ fontSize: 44, color: C.red }} /><Typography sx={{ fontWeight: 900, mt: 1 }}>Unable to enter this meeting</Typography><Button sx={{ mt: 2, color: '#fff', borderColor: C.line }} variant="outlined" onClick={() => signOut(auth).then(() => bootstrap())}>Back to sign in</Button></Box></Box>;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: C.bg, color: C.text, p: { xs: 1, md: 1.3 }, display: 'grid', gridTemplateRows: 'auto 1fr auto', gap: 1 }}>
      <Paper elevation={0} sx={{ p: 1.1, bgcolor: C.panel, color: C.text, borderRadius: 3, border: `1px solid ${C.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: .8 }}><Box sx={{ width: 35, height: 35, borderRadius: 2.2, bgcolor: 'rgba(75,163,211,.14)', color: C.blue, display: 'grid', placeItems: 'center' }}><VideoCall /></Box><Box><Typography sx={{ fontWeight: 900, fontSize: '.85rem' }}>{meeting.title}</Typography><Typography sx={{ color: C.soft, fontSize: '.62rem' }}>{meeting.joinCode || 'Internal room'} · {meeting.duration || 30} min</Typography></Box></Box>
        <Box sx={{ display: 'flex', gap: .55, alignItems: 'center' }}><Chip icon={<CheckCircle />} size="small" label="Encrypted browser session" sx={{ bgcolor: 'rgba(66,184,155,.13)', color: C.green, fontWeight: 800, '& .MuiChip-icon': { color: C.green } }} /><Chip icon={<People />} size="small" label={`${Object.keys(participants).length} participant${Object.keys(participants).length === 1 ? '' : 's'}`} sx={{ bgcolor: 'rgba(255,255,255,.06)', color: C.text, '& .MuiChip-icon': { color: C.soft } }} /></Box>
      </Paper>

      <Box sx={{ minHeight: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) 310px' }, gap: 1 }}>
        <Box sx={{ minWidth: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: Object.keys(remoteStreams).length ? 'repeat(2,minmax(0,1fr))' : '1fr' }, gap: 1, alignContent: 'start', overflowY: 'auto' }}>
          {localStream && <VideoTile stream={localStream} label={`${userData?.displayName || 'You'} (You)${sharing ? ' · Screen' : ''}`} muted />}
          {Object.entries(remoteStreams).map(([uid, stream]) => <VideoTile key={uid} stream={stream} label={participants[uid]?.displayName || 'Employee'} />)}
        </Box>

        <Paper elevation={0} sx={{ bgcolor: C.panel, color: C.text, borderRadius: 3, border: `1px solid ${C.line}`, display: 'grid', gridTemplateRows: 'auto 1fr auto', minHeight: { xs: 300, lg: 0 }, overflow: 'hidden' }}>
          <Box sx={{ p: 1.2, display: 'flex', gap: .8, alignItems: 'center' }}><Chat fontSize="small" /><Typography sx={{ fontWeight: 900 }}>Meeting chat</Typography></Box><Divider sx={{ borderColor: C.line }} />
          <Box sx={{ p: 1.1, overflowY: 'auto' }}>{chat.map((row) => <Box key={row.id} sx={{ mb: 1, p: .8, borderRadius: 2, bgcolor: row.uid === currentUid ? 'rgba(75,163,211,.15)' : 'rgba(255,255,255,.05)' }}><Typography sx={{ fontSize: 10, fontWeight: 900, color: C.blue }}>{row.name || participants[row.uid]?.displayName || 'Employee'}</Typography><Typography sx={{ fontSize: 11.5, color: C.text, whiteSpace: 'pre-wrap' }}>{row.message}</Typography></Box>)}</Box>
          <Box sx={{ p: 1, display: 'flex', gap: .6 }}><TextField size="small" fullWidth placeholder="Message" value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }} sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }} /><IconButton onClick={sendChat} sx={{ color: '#fff', bgcolor: C.blue, '&:hover': { bgcolor: C.blue } }}><Send /></IconButton></Box>
        </Paper>
      </Box>

      <Paper elevation={0} sx={{ p: 1, bgcolor: C.panel, color: C.text, borderRadius: 3, border: `1px solid ${C.line}`, display: 'flex', justifyContent: 'center', gap: .7, flexWrap: 'wrap' }}>
        <Button onClick={toggleMic} startIcon={micOn ? <GraphicEq /> : <VolumeOff />} sx={{ color: '#fff', bgcolor: micOn ? C.panel2 : C.red, '&:hover': { bgcolor: micOn ? C.panel2 : C.red } }}>{micOn ? 'Mute' : 'Unmute'}</Button>
        <Button onClick={toggleCam} startIcon={<Videocam />} sx={{ color: '#fff', bgcolor: camOn ? C.panel2 : C.red, '&:hover': { bgcolor: camOn ? C.panel2 : C.red } }}>{camOn ? 'Camera off' : 'Camera on'}</Button>
        <Button onClick={shareScreen} startIcon={<Share />} sx={{ color: '#fff', bgcolor: sharing ? C.green : C.panel2, '&:hover': { bgcolor: sharing ? C.green : C.panel2 } }}>{sharing ? 'Stop share' : 'Share screen'}</Button>
        <Button onClick={() => leave(false)} startIcon={<Logout />} sx={{ color: '#fff', bgcolor: C.red, '&:hover': { bgcolor: C.red } }}>Leave</Button>
        {isHost && <Button onClick={() => leave(true)} startIcon={<Close />} sx={{ color: '#fff', bgcolor: '#9A2E2A', '&:hover': { bgcolor: '#9A2E2A' } }}>End meeting</Button>}
      </Paper>
    </Box>
  );
}
